{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            Copyright (c) 2019 - 2024                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.Devices;

{$DEFINE NOPP}

interface

uses
  Classes, SysUtils, WEBLib.Controls, Web, JS,
  WEBLib.ExtCtrls, WEBLib.Graphics, libfilereader, libglmatrix, libmedia;

type
  TCameraType = (ctFront, ctRear, ctSelected);
  TCameraBaseFormat = (cbfJPEG, cbfPNG);

  TCameraDevice = class(TCollectionItem)
  private
    FId: string;
    FName: string;
  public
    procedure Assign(Source: TPersistent); override;
    property Id: string read FId write FId;
    property Name: string read FName write FName;
  end;

  TCameraDevices = class(TCollection)
  private
    procedure SetItem(Index: Integer; const Value: TCameraDevice); reintroduce;
    function GetItem(Index: Integer): TCameraDevice; reintroduce;
  public
    constructor Create;
    function Add: TCameraDevice; reintroduce;
    function GetDeviceById(Id: string): TCameraDevice;
    function GetDeviceByName(Name: string): TCameraDevice;
    function Insert(Index: Integer): TCameraDevice; reintroduce;
    property Items[Index: integer]: TCameraDevice read GetItem write SetItem;
  end;

  TCameraErrorEvent = procedure(Sender: TObject; AName: string; AMessage: string) of object;
  TCameraEvent = procedure(Sender: TObject; ACamera: TCameraDevice) of object;
  TCameraBeforeStartEvent = procedure(Sender: TObject; var AConstraints: TJSObjectRecord) of object;

  TCamera = class(TCustomControl)
  private
    FPaused: Boolean;
    FDeviceCounter: Integer;
    FCameraDevices: TCameraDevices;
    FCameraBaseFormat: TCameraBaseFormat;
    FSelectedCameraDevice: TCameraDevice;
    FOnCameraDevicesInitialized: TNotifyEvent;
    FCameraType: TCameraType;
    FOnCameraStop: TCameraEvent;
    FOnSwitchCamera: TCameraEvent;
    FOnCameraPause: TCameraEvent;
    FOnCameraResume: TCameraEvent;
    FOnCameraStreamPlay: TCameraEvent;
    FOnBeforeStart: TCameraBeforeStartEvent;
    FVideoTrack: TWebMediaStreamTrack;
    FBrowserConstraints: TStringList;
    FDeviceConstraints: TStringList;
    FOnError: TCameraErrorEvent;
    function GetSnapShotBase64: string;
    function GetSnapShotImgData: TJSImageData;
    function GetSnapShotUint8Array: TJSUint8Array;
    procedure SetCameraType(const Value: TCameraType);
  protected
    procedure BindEvents; override;
    function CreateElement: TJSElement; override;
    procedure UpdateElementData; override;
    procedure UpdateElementVisual; override;
    function GetVideoTrack: TWebMediaStreamTrack;
    function HandleStreamPlay(aEvent : TJSPointerEvent): Boolean;
    function HandleEnumerateDevices(aMediaDevices: JSValue): JSValue;
    function HandleUserMedia(aMediaStream: JSValue): JSValue;
    function HandleReject(AValue: JSValue): JSValue;
    function HandleLoadedMetaData(Event: TEventListenerEvent): Boolean;
    procedure UnbindEvents; override;
  public
    destructor Destroy; override;
    procedure CreateInitialize; override;
    function BrowserSupportedConstraints: TStringList;
    function GetDeviceConstraints: TStringList;
    procedure ReInitializeDevices;
    procedure Pause;
    procedure Resume;
    procedure SetSelectedCameraDevice(aDevice: TCameraDevice);
    function ApplyConstraints(AConstraints: TJSObject): TJSPromise;
    procedure Start;
    function StartP: TJSPromise;
    procedure Stop;
    property CameraDevices: TCameraDevices read FCameraDevices;
    property Paused: Boolean read FPaused;
    property SnapShotAsBase64: string read GetSnapShotBase64;
    property SnapShotAsImageData: TJSImageData read GetSnapShotImgData;
    property SnapShotAsUint8Array: TJSUint8Array read GetSnapShotUint8Array;
  published
    property Align;
    property AlignWithMargins;
    property Anchors;
    property CameraType: TCameraType read FCameraType write SetCameraType default ctFront;
    property BaseFormat: TCameraBaseFormat read FCameraBaseFormat write FCameraBaseFormat default cbfJPEG;
    property HeightPercent;
    property HeightStyle;
    property Margins;
    property WidthPercent;
    property WidthStyle;
    property OnError: TCameraErrorEvent read FOnError write FOnError;
    property OnBeforeStart: TCameraBeforeStartEvent read FOnBeforeStart write FOnBeforeStart;
    property OnCameraDevicesInitialized: TNotifyEvent read FOnCameraDevicesInitialized write FOnCameraDevicesInitialized;
    property OnCameraPause: TCameraEvent read FOnCameraPause write FOnCameraPause;
    property OnCameraResume: TCameraEvent read FOnCameraResume write FOnCameraResume;
    property OnCameraStreamPlay: TCameraEvent read FOnCameraStreamPlay write FOnCameraStreamPlay;
    property OnCameraStop: TCameraEvent read FOnCameraStop write FOnCameraStop;
    property OnClick;
    property OnDblClick;
    property OnMouseDown;
    property OnMouseEnter;
    property OnMouseLeave;
    property OnMouseMove;
    property OnMouseUp;
    property OnSwitchCamera: TCameraEvent read FOnSwitchCamera write FOnSwitchCamera;
  end;

  TWebCamera = class(TCamera);

  TMediaCaptureType = (mctVideo, mctAudio, mctBoth);
  TMediaCaptureRecordingMode = (mrmManual, mrmAuto);
  TMediaCaptureFFTSize = (mfs32, mfs64, mfs128, mfs256, mfs512, mfs1024, mfs2048, mfs4096, mfs8192, mfs16384, mfs32768);
  TMediaCaptureCloseEvent = procedure(Sender: TObject; ABinary: TJSUint8Array; ABase: string) of object;

  TMediaCapture = class(TComponent)
  private
    FCamera: TCamera;
    FCapture: TMediaCaptureType;
    FRecorder: TWebMediaRecorder;
    FData: TJSArray;
    FAutoDataArray: TJSUint8Array;
    FAudioTrack: TWebMediaStreamTrack;
    FVideoTrack: TWebMediaStreamTrack;
    FOnStartCapture: TNotifyEvent;
    FOnStopCapture: TMediaCaptureCloseEvent;
    FFormat: string;
    FFFTSize: TMediaCaptureFFTSize;
    FRecordingMode: TMediaCaptureRecordingMode;
    FSensitivity: Integer;
    FTimeConstant: Double;
    FIsRecording: Boolean;
    FAnalyser: TWebAnalyserNode;
    FNewRecorder: Boolean;
    FStopped: Boolean;
    FAudioCtx: TWebAudioContext;
    FAudioContext: TWebAudioContext;
    FOldStartEvent: TCameraEvent;
    procedure SetCamera(const Value: TCamera);
    procedure SetCapture(const Value: TMediaCaptureType);
    procedure SetFFTSize(const Value: TMediaCaptureFFTSize);
    procedure SetRecordingMode(const Value: TMediaCaptureRecordingMode);
    procedure SetSensitivity(const Value: Integer);
    procedure SetTimeConstant(const Value: Double);
  protected
    procedure CreateAnalyser(AStream: TWebMediaStream);
    procedure DoAutoProcess(aTime: TJSDOMHighResTimeStamp);
    procedure DoHandleCameraStart(Sender: TObject; ACamera: TCameraDevice);
    procedure DoHandleCaptureStop;
    function DoHandleDataAvailable(Event: TEventListenerEvent): Boolean;
    procedure GetAudioTrack;
    function GetFFTSize(ASize: TMediaCaptureFFTSize): Integer;
    function GetMinDecibels(ASensitivity: Integer): Integer;
    function GetTimeConstant(AConstant: Double): Double;
    procedure GetVideoTrack;
    function HandleAudioUserMedia(aMediaStream: JSValue): JSValue;
    function HandleFileReaderLoad(Event: TEventListenerEvent): Boolean;
    function HandleVideoUserMedia(aMediaStream: JSValue): JSValue;
    procedure InitializeContext(AStream: TWebMediaStream);
    procedure InitializeRecording;
    procedure Notification(AComponent: TComponent; Operation: TOperation); override;
    procedure RequestDevices;
    procedure RequestVideoAccess;
    procedure StartRecording;
    procedure StopAuto;
    procedure StopManual;
  public
    constructor Create(AOwner: TComponent); override;
    procedure Start;
    procedure Stop;
  published
    property Capture: TMediaCaptureType read FCapture write SetCapture default mctBoth;
    property Camera: TCamera read FCamera write SetCamera;
    property FFTSize: TMediaCaptureFFTSize read FFFTSize write SetFFTSize default mfs2048;
    property RecordingMode: TMediaCaptureRecordingMode read FRecordingMode write SetRecordingMode default mrmManual;
    property Sensitivity: Integer read FSensitivity write SetSensitivity default 100;
    property SmoothingTimeConstant: Double read FTimeConstant write SetTimeConstant; //default 0.9
    property OnStartCapture: TNotifyEvent read FOnStartCapture write FOnStartCapture;
    property OnStopCapture: TMediaCaptureCloseEvent read FOnStopCapture write FOnStopCapture;
  end;

  TWebMediaCapture = class(TMediaCapture);

  TDecoderDecodedEvent = procedure(Sender: TObject; ADecoded: string) of object;

  TDecoder = class(TComponent)
  private
    FTimer: TWebTimer;
    FCamera: TCamera;
    FOnDecoded: TDecoderDecodedEvent;
    FEnableTimer: Boolean;
    FImageControl: TImageControl;
    procedure SetCamera(const Value: TCamera);
    procedure SetEnableTimer(const Value: Boolean);
  protected
    procedure DecodeURL(AURL: string); virtual;
    procedure DoDecoded; virtual;
    procedure DoDecodeTimer(Sender: TObject);
    procedure Notification(AComponent: TComponent; Operation: TOperation); override;
    property Camera: TCamera read FCamera write SetCamera;
    property ImageControl: TImageControl read FImageControl write FImageControl;
    property EnableTimer: Boolean read FEnableTimer write SetEnableTimer default False;
    property OnDecoded: TDecoderDecodedEvent read FOnDecoded write FOnDecoded;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
  end;

  TQRDecoder = class(TDecoder)
  private
    FImg: TJSHTMLImageElement;
    FObj: TWebJSQRResultObject;
  protected
    procedure DoDecoded; override;
    function HandleImageLoad(Event: TEventListenerEvent): boolean;
  public
    procedure DecodeURL(AURL: string); override;
  published
    property Camera;
    property EnableTimer;
    property OnDecoded;
  end;

  TWebQRDecoder = class(TQRDecoder);

  TBarcodeDecoderReader = (bcvCode128, bcvEAN, bcvEAN8, bcvCode39, bcvCode39VIN, bcvCodabar, bcvUPC, bcvUPCE, bcvI2OF5, bcv2OF5, bcvCode93);
  TBarcodeDecoderReaders = set of TBarcodeDecoderReader;

  TBarcodeDecoder = class(TDecoder)
    FTempDisable: Boolean;
    FInternalTimer: Boolean;
    FReader: TBarcodeDecoderReaders;
    FObj: TWebQuaggaJSResultObject;
    FConfigObj: TWebQuaggaJSConfigObject;
  private
    procedure SetReader(const Value: TBarcodeDecoderReaders);
  protected
    procedure DoDecoded; override;
    procedure DoDecodeCallback(aResult: TWebQuaggaJSResultObject);
    procedure InitConfigObj;
  public
    constructor Create(AOwner: TComponent); override;
    procedure DecodeURL(AURL: string); override;
  published
    property Camera;
    property EnableTimer;
    property Reader: TBarcodeDecoderReaders read FReader write SetReader default [bcvCode128];
    property OnDecoded;
  end;

  TWebBarcodeDecoder = class(TBarcodeDecoder);

function Uint8ToBase64(AArray: TJSUint8Array): string;
function IsSafari: Boolean;

var
  TBarcodeDecoderValue: array[TBarcodeDecoderReader] of string = ('code_128_reader', 'ean_reader', 'ean_8_reader', 'code_39_reader', 'code_39_vin_reader',
    'codabar_reader', 'upc_reader', 'upc_e_reader', 'i2of5_reader', '2of5_reader', 'code_93_reader');

implementation

uses
  Types;

function IsSafari: Boolean;
var
  ua: Boolean;
begin
  ua := False;
  asm
    function getMobileOperatingSystem() {
      var userAgent = navigator.userAgent || navigator.vendor || window.opera;

        if (/windows phone/i.test(userAgent)) {
            return "Windows Phone";
        }

        if (/android/i.test(userAgent)) {
            return "Android";
        }

        if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
            return "iOS";
        }

        return "unknown";
    }

    var isSafariMobile = getMobileOperatingSystem();
    var isSafariDesktop = window.safari !== undefined;

    if (isSafariMobile === "iOS" || isSafariDesktop) {
      ua = true;
    }
  end;
  Result := ua;
end;

function Uint8ToBase64(AArray: TJSUint8Array): string;
var
  len, I: Integer;
  base: string;
begin
  len := AArray.byteLength;
  for I := 0 to len - 1 do
    base := base + TJSString.fromCharCode(AArray[i]);

  Result := window.btoa(base);
end;

{ TWebCamera }

function TCamera.ApplyConstraints(AConstraints: TJSObject): TJSPromise;
begin
  Result := nil;
  if Assigned(FVideoTrack) then
  begin
    Result := FVideoTrack.applyConstraints(AConstraints);
  end;
end;

procedure TCamera.BindEvents;
begin
  inherited;
  if Assigned(ElementHandle) then
  begin
    ElementHandle.onplay := @HandleStreamPlay;
  end;
end;

function TCamera.BrowserSupportedConstraints: TStringList;
var
  strArr: TStringDynArray;
  O: TJSObject;
  I: Integer;
begin
  if not Assigned(FBrowserConstraints) then
  begin
    FBrowserConstraints := TStringList.Create;
    O := TWebMediaDevices(window.navigator.mediaDevices).getSupportedConstraints;
    strArr := TJSObject.keys(O);
    for I := 0 to Length(strArr) - 1 do
      FBrowserConstraints.Add(strArr[I]);
  end;
  Result := FBrowserConstraints;
end;

function TCamera.CreateElement: TJSElement;
var
  LLabel: TJSHTMLElement;
begin
  if (csDesigning in ComponentState) then
  begin
    Result := document.createElement('DIV');
    LLabel := TJSHTMLElement(document.createElement('DIV'));
    LLabel.innerHTML := 'TWebCamera';
    BorderStyle := bsSingle;
    LLabel['align'] := 'center';
    LLabel.style.setProperty('border','1px solid gray');
    LLabel.style.setProperty('vertical-align','middle');
    LLabel.style.setProperty('display','table-cell');
    Result.appendChild(LLabel);
  end
  else
  begin
    Result := document.createElement('VIDEO');
    Result.setAttribute('playsinline', '');
  end;
end;

procedure TCamera.UpdateElementData;
begin
  inherited;
  if (csDesigning in ComponentState) then
    TJSHTMLElement(ElementHandle).style.setProperty('display','table');
end;

procedure TCamera.UpdateElementVisual;
begin
  inherited;
  if (csDesigning in ComponentState) then
    TJSHTMLElement(ElementHandle).style.setProperty('background-color','silver');
end;


destructor TCamera.Destroy;
begin
  FCameraDevices.Free;
  if Assigned(FBrowserConstraints) then
    FBrowserConstraints.Free;

  if Assigned(FDeviceConstraints) then
    FDeviceConstraints.Free;

  inherited;
end;

procedure TCamera.CreateInitialize;
begin
  inherited;
  FPaused := True;
  FSelectedCameraDevice := TCameraDevice.Create;
  FCameraDevices := TCameraDevices.Create;
  FDeviceCounter := 1;
  FCameraType := ctFront;
  FCameraBaseFormat := cbfJPEG;
  TabStop := false;

  Width := 400;
  Height := 300;
  if Assigned(TWebMediaDevices(window.navigator.mediaDevices)) then
    TWebMediaDevices(window.navigator.mediaDevices).enumerateDevices._then(@HandleEnumerateDevices);
end;

function TCamera.GetDeviceConstraints: TStringList;
var
  strArr: TStringDynArray;
  O: TJSObject;
  I: Integer;
begin
  if not Assigned(FDeviceConstraints) then
    FDeviceConstraints := TStringList.Create;

  if Assigned(FVideoTrack) then
  begin
    FDeviceConstraints.Clear;
    O := FVideoTrack.getCapabilities;
    strArr := TJSObject.keys(O);
    for I := 0 to Length(strArr) - 1 do
      FDeviceConstraints.Add(strArr[I]);
  end;
  Result := FDeviceConstraints;
end;

function TCamera.GetSnapShotBase64: string;
var
  canvas: TJSHTMLCanvasElement;
  vid: TWebHTMLVideoElement;
begin
  Result := '';

  if Assigned(ElementHandle) then
  begin
    vid := TWebHTMLVideoElement(ElementHandle);
    canvas := TJSHTMLCanvasElement(document.createElement('canvas'));
    canvas.width := vid.videoWidth;
    canvas.height := vid.videoHeight;
    canvas.getContextAs2DContext('2d').drawImage(TJSObject(vid), 0, 0);

    case FCameraBaseFormat of
      cbfJPEG: Result := canvas.toDataURL('image/jpeg');
      cbfPNG: Result := canvas.toDataURL('image/png');
    end;
  end;
end;

function TCamera.GetSnapShotImgData: TJSImageData;
var
  canvas: TJSHTMLCanvasElement;
  ctx: TJSCanvasRenderingContext2D;
  vid: TWebHTMLVideoElement;
begin
  Result := nil;

  if Assigned(ElementHandle) then
  begin
    vid := TWebHTMLVideoElement(ElementHandle);
    canvas := TJSHTMLCanvasElement(document.createElement('canvas'));
    canvas.width := vid.videoWidth;
    canvas.height := vid.videoHeight;
    ctx := canvas.getContextAs2DContext('2d');
    ctx.drawImage(TJSObject(vid), 0, 0);

    Result := ctx.getImageData(0, 0, canvas.width, canvas.height);
  end;
end;

function TCamera.GetSnapShotUint8Array: TJSUint8Array;
var
  b, binaryString: TJSString;
  str: string;
  exp: TJSRegexp;
  len, I: Integer;
begin
  b := TJSString.new(GetSnapShotBase64);
  exp := TJSRegexp.new('^data:[\s\S]*;base64,');
  str := b.replace(exp, '');
  binaryString := TJSString.new(window.atob(str));
  len := binaryString.length;
  Result := TJSUint8Array.new(len);

  for I := 0 to len - 1 do
    Result[I] := binaryString.charCodeAt(I);
end;

function TCamera.HandleEnumerateDevices(aMediaDevices: JSValue): JSValue;
  function DoEachDevice(mediadevice: JSValue; index: NativeInt; anArray: TJSArray): Boolean;
  var
    cd: TCameraDevice;
    mi: TWebMediaDevicesInfo;
  begin
    mi := TWebMediaDevicesInfo(mediadevice);
    if mi.kind = 'videoinput' then
    begin
      cd := FCameraDevices.Add;
      cd.Id := mi.deviceId;

      if mi.&label <> '' then
        cd.Name := mi.&label
      else
      begin
        cd.Name := 'Camera ' + IntToStr(FDeviceCounter);
        Inc(FDeviceCounter);
      end;
    end;

    Result := True;
  end;
begin
  TJSArray(aMediaDevices).forEach(@DoEachDevice);

  if Assigned(OnCameraDevicesInitialized) then
    OnCameraDevicesInitialized(Self);

  Result := True;
end;

function TCamera.GetVideoTrack: TWebMediaStreamTrack;
begin
  Result := FVideoTrack;
end;

function TCamera.HandleLoadedMetaData(Event: TEventListenerEvent): Boolean;
begin
  if Assigned(ElementHandle) then
    TWebHTMLMediaElement(ElementHandle).play;

  Result := True;
end;

function TCamera.HandleReject(AValue: JSValue): JSValue;
var
  n, m: string;
begin
  Result := nil;
  if AValue is TJSDOMException then
  begin
    m := TJSDOMException(AValue).message;
    n := TJSDOMException(AValue).name;
    if Assigned(OnError) then
      OnError(Self, n, m);
  end;
end;

function TCamera.HandleStreamPlay(aEvent: TJSPointerEvent): Boolean;
begin
  if Assigned(OnCameraStreamPlay) then
    OnCameraStreamPlay(Self, FSelectedCameraDevice);

  Result := True;

  FPaused := False;
end;

function TCamera.HandleUserMedia(aMediaStream: JSValue): JSValue;
begin
  TWebHTMLMediaElement(ElementHandle).srcObject := TJSObject(aMediaStream);
  TWebHTMLMediaElement(ElementHandle).onloadedmetadata := @HandleLoadedMetaData;
  FVideoTrack := TWebMediaStreamTrack(TWebMediaStream(aMediaStream).getVideoTracks[0]);

  Result := True;
end;

procedure TCamera.Pause;
begin
  FPaused := True;

  if Assigned(ElementHandle) then
  begin
    TWebHTMLMediaElement(ElementHandle).pause;

    if Assigned(OnCameraPause) then
      OnCameraPause(Self, FSelectedCameraDevice);
  end;
end;

procedure TCamera.ReInitializeDevices;
begin
  CameraDevices.Clear;
  if Assigned(TWebMediaDevices(window.navigator.mediaDevices)) then
    TWebMediaDevices(window.navigator.mediaDevices).enumerateDevices._then(@HandleEnumerateDevices);
end;

procedure TCamera.Resume;
begin
  if Assigned(ElementHandle) and (Paused) then
  begin
    TWebHTMLMediaElement(ElementHandle).play;

    if Assigned(OnCameraResume) then
      OnCameraResume(Self, FSelectedCameraDevice);
  end;
  FPaused := False;
end;

procedure TCamera.SetCameraType(const Value: TCameraType);
begin
  if FCameraType <> Value then
    FCameraType := Value;
end;

procedure TCamera.SetSelectedCameraDevice(aDevice: TCameraDevice);
begin
  if aDevice.Id <> FSelectedCameraDevice.Id then
  begin
    FSelectedCameraDevice.Assign(aDevice);

    if Assigned(OnSwitchCamera) then
      OnSwitchCamera(Self, FSelectedCameraDevice);
  end;
end;

procedure TCamera.Start;
var
  p: TJSPromise;
begin
  p := StartP;
  p.catch(@HandleReject);
end;

function TCamera.StartP: TJSPromise;
var
  O, P: TJSObject;
  LObjRec: TJSObjectRecord;
begin
  Stop;

  case FCameraType of
    ctFront: O := new(['facingMode', 'user']);
    ctRear: O := new(['facingMode', 'environment']);
    ctSelected:
    begin
      if FSelectedCameraDevice.Id <> '' then
      begin
        P := new(['exact', FSelectedCameraDevice.Id]);
        O := new(['deviceId', P]);
      end
      else
        O := new([]);
    end;
  end;

  if Assigned(OnBeforeStart) then
  begin
    LObjRec.jsobject := O;
    OnBeforeStart(Self, LObjRec);
  end;

  if Assigned(ElementHandle) and Assigned(TWebMediaDevices(window.navigator.mediaDevices)) then
    Result := TWebMediaDevices(window.navigator.mediaDevices).getUserMedia(new(['video', O]))._then(@HandleUserMedia){.catch(@HandleReject)}
  else
    Result := nil;
end;

procedure TCamera.Stop;
  function DoEachTrack(track: JSValue; index: NativeInt; anArray: TJSArray): Boolean;
  begin
    TWebMediaStreamTrack(track).stop;
    Result := True;
  end;
begin
  FPaused := True;

  if Assigned(ElementHandle) then
  begin
    if Assigned(TWebHTMLMediaElement(ElementHandle).srcObject) then
    begin
      TWebMediaStream(TWebHTMLMediaElement(ElementHandle).srcObject).getVideoTracks.forEach(@DoEachTrack);

      if Assigned(OnCameraStop) then
        OnCameraStop(Self, FSelectedCameraDevice);
    end;
  end;
end;

procedure TCamera.UnbindEvents;
begin
  inherited;
  if Assigned(ElementHandle) then
    ElementHandle.onplay := nil;
end;

{ TCameraDevices }

function TCameraDevices.Add: TCameraDevice;
begin
  Result := TCameraDevice(inherited Add);
end;

constructor TCameraDevices.Create;
begin
  inherited Create(TCameraDevice);
end;

function TCameraDevices.GetDeviceById(Id: string): TCameraDevice;
var
  I: Integer;
begin
  Result := nil;

  for I := 0 to Count - 1 do
  begin
    if TCameraDevice(Items[I]).Id = Id then
    begin
      Result := TCameraDevice(Items[I]);
      Exit;
    end;
  end;
end;

function TCameraDevices.GetDeviceByName(Name: string): TCameraDevice;
var
  I: Integer;
begin
  Result := nil;

  for I := 0 to Count - 1 do
  begin
    if TCameraDevice(Items[I]).Name = Name then
    begin
      Result := TCameraDevice(Items[I]);
      Exit;
    end;
  end;
end;

function TCameraDevices.GetItem(Index: Integer): TCameraDevice;
begin
  Result := TCameraDevice(inherited Items[Index]);
end;

function TCameraDevices.Insert(Index: Integer): TCameraDevice;
begin
  Result := TCameraDevice(inherited Insert(Index));
end;

procedure TCameraDevices.SetItem(Index: Integer; const Value: TCameraDevice);
begin
  inherited Items[Index] := Value;
end;

{ TCameraDevice }

procedure TCameraDevice.Assign(Source: TPersistent);
begin
  if (Source is TCameraDevice) then
  begin
    FName := (Source as TCameraDevice).Name;
    FId := (Source as TCameraDevice).Id;
  end;
end;

{ TDecoder }

constructor TDecoder.Create(AOwner: TComponent);
begin
  inherited;
  FTimer := TWebTimer.Create(Self);
  FEnableTimer := False;
end;

procedure TDecoder.DecodeURL(AURL: string);
begin
  //
end;

destructor TDecoder.Destroy;
begin
  FTimer.Free;
  inherited;
end;

procedure TDecoder.DoDecoded;
begin
end;

procedure TDecoder.DoDecodeTimer(Sender: TObject);
begin
  if Assigned(FCamera) and not FCamera.Paused then
    DoDecoded;
end;

procedure TDecoder.Notification(AComponent: TComponent; Operation: TOperation);
begin
  inherited;
  if not (csDestroying in ComponentState) then
  begin
    if (Operation = opRemove) then
    begin
      if (AComponent = FCamera) then
        FCamera := nil;
    end;
  end;
end;

procedure TDecoder.SetCamera(const Value: TCamera);
begin
  if FCamera <> Value then
    FCamera := Value;
end;

procedure TDecoder.SetEnableTimer(const Value: Boolean);
begin
  if FEnableTimer <> Value then
  begin
    FEnableTimer := Value;

    if FEnableTimer then
      FTimer.OnTimer := DoDecodeTimer
    else
      FTimer.OnTimer := nil;
  end;
end;

{ TQRDecoder }

procedure TQRDecoder.DecodeURL(AURL: string);
begin
  inherited;
  FImg := TJSHTMLImageElement(document.createElement('IMG'));

  FImg.onload := @HandleImageLoad;
  FImg.src := AURL;
end;

procedure TQRDecoder.DoDecoded;
var
  img: TJSImageData;
begin
  img := Camera.SnapshotAsImageData;
  FObj := jsQR(img.data, img.width, img.height);

  if Assigned(FObj) and Assigned(OnDecoded) then
    OnDecoded(Self, FObj.data);
end;

function TQRDecoder.HandleImageLoad(Event: TEventListenerEvent): boolean;
var
  canvas: TJSHTMLCanvasElement;
  ctx: TJSCanvasRenderingContext2D;
  imgData: TJSImageData;
begin
  canvas := TJSHTMLCanvasElement(document.createElement('canvas'));
  canvas.width := FImg.width;
  canvas.height := FImg.height;
  ctx := canvas.getContextAs2DContext('2d');
  ctx.drawImage(TJSObject(FImg), 0, 0);

  imgData := ctx.getImageData(0, 0, canvas.width, canvas.height);
  FObj := jsQR(imgData.data, imgData.width, imgData.height);

  if Assigned(FObj) and Assigned(OnDecoded) then
    OnDecoded(Self, FObj.data);

  Result := True;
end;

{ TBarcodeDecoder }

constructor TBarcodeDecoder.Create(AOwner: TComponent);
begin
  inherited;
  FConfigObj := TWebQuaggaJSConfigObject.new;
  FReader := [bcvCode128];
  FTempDisable := False;
  FInternalTimer := True;
end;

procedure TBarcodeDecoder.DecodeURL(AURL: string);
begin
  inherited;
  FConfigObj.src := AURL;
  if EnableTimer then
  begin
    EnableTimer := False;
    FTempDisable := True;
  end;
  Quagga.decodeSingle(FConfigObj, @DoDecodeCallback);
end;

procedure TBarcodeDecoder.DoDecodeCallback(aResult: TWebQuaggaJSResultObject);
begin
  if Assigned(aResult) then
  begin
    if Assigned(aResult.codeResult) then
    begin
      if Assigned(OnDecoded) then
        OnDecoded(Self, aResult.codeResult.code)
    end;
  end;

  if FTempDisable then
  begin
    FInternalTimer := True;
    FTempDisable := False;
  end;

  if Assigned(Camera) then
    FInternalTimer := True;
end;

procedure TBarcodeDecoder.DoDecoded;
var
  img: string;
begin
  if FInternalTimer then
  begin
    FInternalTimer := False;
    img := Camera.SnapshotAsBase64;
    FConfigObj.src := img;
    Quagga.decodeSingle(FConfigObj, @DoDecodeCallback);
  end;
end;

procedure TBarcodeDecoder.InitConfigObj;
var
  arr: TJSArray;
  opt: TBarcodeDecoderReader;
begin
  arr := TJSArray.new;
  if FReader = [] then
    arr.push('code_128_reader')
  else
  begin
    for opt := Low(TBarcodeDecoderReaders) to High(TBarcodeDecoderReaders) do
    begin
      if opt in Reader then
        arr.push(TBarcodeDecoderValue[opt]);
    end;
  end;

  FConfigObj.decoder := new(['readers', arr]);
end;

procedure TBarcodeDecoder.SetReader(const Value: TBarcodeDecoderReaders);
begin
  FReader := Value;
  InitConfigObj;
end;

{ TMediaCapture }

constructor TMediaCapture.Create(AOwner: TComponent);
begin
  inherited;
  FData := TJSArray.new;
  FCapture := mctBoth;
  FAudioTrack := nil;
  FVideoTrack := nil;
  FRecordingMode := mrmManual;
  FTimeConstant := 0.9;
  FFFTSize := mfs2048;
  FSensitivity := 100;
  FNewRecorder := True;
  asm
    this.FAudioContext = window.AudioContext || window.webkitAudioContext;
  end;
end;

procedure TMediaCapture.CreateAnalyser(AStream: TWebMediaStream);
var
  mic: TWebMediaStreamAudioSourceNode;
begin
  FAnalyser := FAudioCtx.createAnalyser;
  FAnalyser.MinDecibels := GetMinDecibels(FSensitivity);
  FAnalyser.SmoothingTimeConstant := GetTimeConstant(FTimeConstant);
  FAnalyser.FFTSize := GetFFTSize(FFFTSize);
  mic := FAudioCtx.createMediaStreamSource(AStream);
  mic.connect(FAnalyser);
  FAutoDataArray := TJSUint8Array.new(FAnalyser.frequencyBinCount);
  FIsRecording := False;
  DoAutoProcess(Now);
end;

procedure TMediaCapture.DoAutoProcess(aTime: TJSDOMHighResTimeStamp);
var
  cb: TFrameRequestMethodCallback;
  I, sum, len: Integer;
  av: Double;
begin
  cb := DoAutoProcess;
  TJSAnimWindow.requestAnimationFrame(cb);

  if FStopped then
    Exit;

  if not FNewRecorder then
    Exit;

  FAnalyser.getByteFrequencyData(FAutoDataArray);

  sum := 0;
  len := FAutoDataArray.length;
  for I := 0 to len - 1 do
    sum := sum + FAutoDataArray[I];

  av := sum / len;
  if (av > 1) and (not FIsRecording) then
  begin
    StartRecording;
    FIsRecording := True;
  end
  else if (av <= 1) and FIsRecording then
  begin
    FRecorder.stop;
    FIsRecording := False;
    FNewRecorder := False;
    RequestDevices;
  end;
end;

procedure TMediaCapture.DoHandleCameraStart(Sender: TObject; ACamera: TCameraDevice);
begin
  if Assigned(FOldStartEvent) then
    FOldStartEvent(Sender, ACamera);

  FVideoTrack := FCamera.GetVideoTrack;
  InitializeRecording;
end;

{$HINTS OFF}
procedure TMediaCapture.DoHandleCaptureStop;
var
  fr: TJSFileReader;
begin
  fr := TJSFileReader.new;
  fr.addEventListener('load',@HandleFileReaderLoad);
  asm
    var blob = new Blob(this.FData, {
      type: this.FFormat
    });

    fr.readAsArrayBuffer(blob);
  end;
  FData := TJSArray.new;
end;
{$HINTS ON}

function TMediaCapture.DoHandleDataAvailable(Event: TEventListenerEvent): Boolean;
var
  b: TJSBlob;
begin
  asm
    b = Event.data;
  end;
  FData.push(b);

  Result := True;
end;

procedure TMediaCapture.GetAudioTrack;
begin
  case FCapture of
    mctAudio, mctBoth: TWebMediaDevices(window.navigator.mediaDevices).getUserMedia(new(['audio', True]))._then(@HandleAudioUserMedia);
  end;
end;

function TMediaCapture.GetFFTSize(ASize: TMediaCaptureFFTSize): Integer;
begin
  Result := 2048;

  case ASize of
    mfs32: Result := 32;
    mfs64: Result := 64;
    mfs128: Result := 128;
    mfs256: Result := 256;
    mfs512: Result := 512;
    mfs1024: Result := 1024;
    mfs2048: Result := 2048;
    mfs4096: Result := 4096;
    mfs8192: Result := 8192;
    mfs16384: Result := 16384;
    mfs32768: Result := 32768;
  end;
end;

function TMediaCapture.GetMinDecibels(ASensitivity: Integer): Integer;
begin
  if ASensitivity < 0 then
    ASensitivity := 0
  else if ASensitivity > 100 then
    ASensitivity := 100;

  if ASensitivity = 0 then
    Result := ASensitivity
  else
    Result := -ASensitivity;
end;

function TMediaCapture.GetTimeConstant(AConstant: Double): Double;
begin
  if AConstant < 0.0 then
    AConstant := 0.0
  else if AConstant >= 1.0 then
    AConstant := 0.99;

  Result := AConstant;
end;

procedure TMediaCapture.GetVideoTrack;
begin
  case FCapture of
    mctVideo, mctBoth:
    begin
      if Assigned(FCamera) then
        FCamera.Start
      else
        RequestVideoAccess;
    end;
  end;
end;

function TMediaCapture.HandleAudioUserMedia(aMediaStream: JSValue): JSValue;
begin
  FAudioTrack := TWebMediaStreamTrack(TWebMediaStream(aMediaStream).getAudioTracks[0]);
  InitializeRecording;
  Result := True;
end;

function TMediaCapture.HandleFileReaderLoad(Event: TEventListenerEvent): Boolean;
var
  u: TJSUint8Array;
  b: string;
begin
  asm
    u = new Uint8Array(Event.target.result);
  end;

  b := Uint8ToBase64(u);

  if Assigned(OnStopCapture) then
    OnStopCapture(Self, u, b);

  Result := True;
end;

function TMediaCapture.HandleVideoUserMedia(aMediaStream: JSValue): JSValue;
begin
  FVideoTrack := TWebMediaStreamTrack(TWebMediaStream(aMediaStream).getVideoTracks[0]);
  InitializeRecording;
  Result := True;
end;

procedure TMediaCapture.InitializeContext(AStream: TWebMediaStream);
begin
  if not Assigned(FAudioContext) then
    Exit;

  if not Assigned(FAudioCtx) then
  begin
    asm
      this.FAudioCtx = new this.FAudioContext();
    end;
  end;

  CreateAnalyser(AStream);
end;

procedure TMediaCapture.InitializeRecording;
var
  mstream: TWebMediaStream;
  opt: TJSObject;
begin
  if (FCapture = mctBoth) and (not Assigned(FVideoTrack) or  not Assigned(FAudioTrack)) then
    Exit;

  mstream := TWebMediaStream.new;

  case FCapture of
    mctAudio:
    begin
      FFormat := 'audio/';
      mstream.addTrack(FAudioTrack);
    end;
    mctBoth:
    begin
      FFormat := 'video/';
      mstream.addTrack(FAudioTrack);
      mstream.addTrack(FVideoTrack);
    end;
    mctVideo:
    begin
      FFormat := 'video/';
      mstream.addTrack(FVideoTrack);
    end;
  end;

  if IsSafari and (FFormat = 'video/') then
    FFormat := FFormat + 'mp4'
  else
    FFormat := FFormat + 'webm';

  opt := new(['mimeType', FFormat]);

  FRecorder := TWebMediaRecorder.new(mstream, opt);
  FRecorder.ondataavailable := @DoHandleDataAvailable;
  FRecorder.onstop := @DoHandleCaptureStop;

  case FRecordingMode of
    mrmManual: StartRecording;
    mrmAuto:
    begin
      FNewRecorder := True;
      InitializeContext(mstream);
    end;
  end;
end;

procedure TMediaCapture.Notification(AComponent: TComponent;
  Operation: TOperation);
begin
  inherited;
  if not (csDestroying in ComponentState) then
  begin
    if (Operation = opRemove) then
    begin
      if (AComponent = FCamera) then
        FCamera := nil;
    end;
  end;
end;

procedure TMediaCapture.RequestDevices;
var
  mr: Boolean;
begin
  mr := False;
  //if MediaRecorder API or MediaDevices are not available then exit
  asm
    if (window.MediaRecorder == undefined) { mr = true; };
  end;

  if (not Assigned(TWebMediaDevices(window.navigator.mediaDevices))) or mr then
    Exit;

  GetVideoTrack;
  GetAudioTrack;
end;

procedure TMediaCapture.RequestVideoAccess;
begin
  TWebMediaDevices(window.navigator.mediaDevices).getUserMedia(new(['video', True]))._then(@HandleVideoUserMedia);
end;

procedure TMediaCapture.SetCamera(const Value: TCamera);
begin
  if FCamera <> Value then
  begin
    FCamera := Value;
    if Assigned(FCamera) then
    begin
      FOldStartEvent := FCamera.OnCameraStreamPlay;
      FCamera.OnCameraStreamPlay := DoHandleCameraStart;
    end
    else
      FOldStartEvent := nil;
  end;
end;

procedure TMediaCapture.SetCapture(const Value: TMediaCaptureType);
begin
  if FCapture <> Value then
    FCapture := Value;
end;

procedure TMediaCapture.SetFFTSize(const Value: TMediaCaptureFFTSize);
begin
  FFFTSize := Value;
end;

procedure TMediaCapture.SetRecordingMode(const Value: TMediaCaptureRecordingMode);
begin
  if FRecordingMode <> Value then
    FRecordingMode := Value;
end;

procedure TMediaCapture.SetSensitivity(const Value: Integer);
begin
  FSensitivity := Value;
end;

procedure TMediaCapture.SetTimeConstant(const Value: Double);
begin
  FTimeConstant := Value;
end;

procedure TMediaCapture.Start;
begin
  case FRecordingMode of
    mrmAuto:
    begin
      if not Assigned(FRecorder) then
        RequestDevices;

      FStopped := False;
    end;
    mrmManual: RequestDevices;
  end;
end;

procedure TMediaCapture.StartRecording;
begin
  FRecorder.start;

  if Assigned(OnStartCapture) then
    OnStartCapture(Self);
end;

procedure TMediaCapture.Stop;
begin
  case FRecordingMode of
    mrmAuto: StopAuto;
    mrmManual: StopManual;
  end;
end;

procedure TMediaCapture.StopAuto;
begin
  FStopped := True;

  if Assigned(FRecorder) and FIsRecording then
  begin
    FIsRecording := False;
    FRecorder.Stop;
  end;
end;

procedure TMediaCapture.StopManual;
begin
  if Assigned(FRecorder) then
    FRecorder.Stop;

  if Assigned(FCamera) then
    FCamera.Stop;

  if Assigned(FAudioTrack) then
  begin
    FAudioTrack.stop;
    FAudioTrack := nil;
  end;

  if Assigned(FVideoTrack) then
  begin
    FVideoTrack.stop;
    FVideoTrack := nil;
  end;
end;

end.
